// Ordem manual por arraste (US-007). A lista continua automática; o que o usuário
// arrastou fica onde ele deixou, e o resto se encaixa pela regra automática.
//  - position: posição relativa na seção no momento do arraste (0 = topo, 1 = fim)
//  - vale também se a tarefa mudar de seção com o tempo (ASM-009: mantém a posição relativa)
//  - tarefas novas ou com prazo editado (position = null) entram onde a regra mandar (AC-184)
import { addDays, toDateKey } from './dates';
import type { SectionKey, TaskBoard } from './ordering';
import type { Task } from './task';

const hasPos = (t: Task) => typeof t.position === 'number';

/**
 * Recebe a seção já na ordem automática e devolve a ordem final: as arrastadas pela
 * posição (empate: ordem automática); cada uma das demais entra logo depois de TODAS
 * as tarefas que a regra automática colocaria antes dela.
 */
export function arrange(autoSorted: readonly Task[]): Task[] {
  if (!autoSorted.some(hasPos)) return [...autoSorted];
  const autoIndex = new Map(autoSorted.map((t, i) => [t.id, i]));
  const result = autoSorted
    .filter(hasPos)
    .sort((a, b) => a.position! - b.position! || autoIndex.get(a.id)! - autoIndex.get(b.id)!);
  autoSorted.forEach((t, i) => {
    if (hasPos(t)) return;
    let after = -1;
    result.forEach((r, j) => {
      if (autoIndex.get(r.id)! < i) after = j;
    });
    result.splice(after + 1, 0, t);
  });
  return result;
}

/** Posições relativas para a nova ordem de uma seção: (i + 1) / (n + 1). */
export function rankPositions(ids: readonly string[]): Map<string, number> {
  return new Map(ids.map((id, i) => [id, (i + 1) / (ids.length + 1)]));
}

/** Seções em que dá para soltar uma tarefa vinda de outra seção (ASM-005). */
export const DROP_SECTIONS: SectionKey[] = ['today', 'tomorrow', 'next7', 'later'];

export type DropPlan =
  /** nada muda (ex.: soltar em "Atrasadas" vindo de outra seção) */
  | { kind: 'none' }
  /** só reordena a seção */
  | { kind: 'reorder'; ids: string[] }
  /** muda o prazo (Hoje/Amanhã, mantendo o horário — ASM-006) e reordena a seção de destino */
  | { kind: 'move'; taskId: string; patch: { due_date: string; due_time: string | null }; ids: string[] }
  /** "Próximos dias" / "Para depois": pede "Informe uma data ou deixe vazio" (AC-182) */
  | { kind: 'askDate'; taskId: string; section: SectionKey; index: number };

function sectionIds(board: TaskBoard, key: SectionKey): string[] {
  return board.sections.find((s) => s.key === key)?.tasks.map((t) => t.id) ?? [];
}

function insertAt(ids: string[], id: string, index: number): string[] {
  const out = ids.filter((x) => x !== id);
  out.splice(Math.max(0, Math.min(index, out.length)), 0, id);
  return out;
}

export function sectionOfTask(board: TaskBoard, taskId: string): SectionKey | null {
  return board.sections.find((s) => s.tasks.some((t) => t.id === taskId))?.key ?? null;
}

/** O que fazer quando a tarefa `taskId` é solta na seção `to`, na posição `index`. */
export function planDrop(board: TaskBoard, taskId: string, to: SectionKey, index: number, now: Date): DropPlan {
  const from = sectionOfTask(board, taskId);
  if (!from) return { kind: 'none' };
  if (from === to) {
    const ids = insertAt(sectionIds(board, to), taskId, index);
    return ids.join() === sectionIds(board, to).join() ? { kind: 'none' } : { kind: 'reorder', ids };
  }
  if (to === 'overdue') return { kind: 'none' };
  const task = board.sections.flatMap((s) => s.tasks).find((t) => t.id === taskId)!;
  if (to === 'today' || to === 'tomorrow') {
    const today = toDateKey(now);
    const due_date = to === 'today' ? today : addDays(today, 1);
    return { kind: 'move', taskId, patch: { due_date, due_time: task.due_time }, ids: insertAt(sectionIds(board, to), taskId, index) };
  }
  return { kind: 'askDate', taskId, section: to, index };
}

/**
 * Depois de "Informe uma data ou deixe vazio": se a data cair na seção onde a tarefa
 * foi solta, ela fica na posição solta; senão vai para a seção da data, pela regra
 * automática ("é para lá que vai").
 */
export function resolveAskDate(
  board: TaskBoard,
  plan: Extract<DropPlan, { kind: 'askDate' }>,
  dueDate: string | null,
  dueTime: string | null,
  sectionFor: (due_date: string | null, due_time: string | null) => SectionKey
): { patch: { due_date: string | null; due_time: string | null }; ids: string[] | null } {
  const patch = { due_date: dueDate, due_time: dueDate ? dueTime : null };
  const lands = sectionFor(patch.due_date, patch.due_time);
  return { patch, ids: lands === plan.section ? insertAt(sectionIds(board, lands), plan.taskId, plan.index) : null };
}
